import { Button } from '@heidi/design-system/button';
import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import { Textarea } from '@heidi/design-system/textarea';
import { toast } from '@heidi/design-system/toast';
import { AlertTriangle } from 'lucide-react';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useIntl, type IntlShape } from 'react-intl';

import {
  isSessionDraftToolName,
  resolveDraftPresentation,
} from '../../../contracts/approval-kinds';
import type { ApprovalVerdict } from '../../../contracts/harness.types';
import type { DesktopControlApprovalPort } from '../../../contracts/projects/desktop-control-approval.types';
import { RUN_COMPUTER_USE_TOOL_NAME } from '../../../data/harness/timeline/derive-is-computer-use';
import { approvalAcceptsNote } from '../../../data/projects/components/approval-note-opt-in';
import { projectRunFailureMessages } from '../../../data/projects/components/projectRunFailure.messages';
import { useProject } from '../../../data/projects/hooks';
import {
  formatDraftHeader,
  resolveDraftHeaderSpec,
} from '../../inline-approval/draft-header';
import { CREATE_SESSION_FROM_EHR_APPOINTMENT_TOOL } from '../../inline-approval/ehr-session-create-approval';
import { EhrSessionCreateApprovalBlock } from '../../inline-approval/ehr-session-create-approval-block';
import { InlineApprovalActions } from '../../inline-approval/inline-approval-actions';
import { InlineApprovalCardShell } from '../../inline-approval/inline-approval-card-shell';
import { LabelledContextPanel } from '../../inline-approval/labelled-context-panel';
import {
  CREATE_PATIENT_PROFILE_TOOL_NAME,
  UPDATE_PATIENT_CONTEXT_TOOL_NAME,
} from '../../inline-approval/patient-context-approval';
import type { PatientContextApprovalPorts } from '../../inline-approval/patient-context-approval-request';
import {
  CreatePatientProfileApprovalRequest,
  PatientContextApprovalRequest,
} from '../../inline-approval/patient-context-approval-request';
import {
  hasPresentationDetails,
  isPatientProfileChangeBody,
  presentationSubject,
  readPresentedHeader,
} from '../../inline-approval/presentation-details';
import { isSessionEmailApprovalTool } from '../../inline-approval/session-email-approval';
import { SessionEmailApprovalCard } from '../../inline-approval/session-email-approval-card';
import {
  blocksOfKind,
  readPresentation,
  type ApprovalPresentation,
} from '../../inline-approval/tool-call-presentation';
import type { ApprovalRequestInput } from './ApprovalRequestBlock.display';
import {
  buildSubtitle,
  buildTitle,
  readApprovalFields,
  REQUEST_HUMAN_APPROVAL_TOOL_NAME,
} from './ApprovalRequestBlock.display';

/**
 * HITL approval card rendered inline in the chat against the
 * originating `tool_call` block.
 *
 * Visual contract matches `ComputerUseTaskBlock`'s awaiting-approval
 * state so the inline approval family feels consistent: the agent's
 * `proposed_action` is the primary title, a humanised tool-family
 * label (plus a subtle ``· High risk`` / ``· Medium risk`` suffix when
 * the agent declared the gate dangerous) sits underneath as the
 * caption, and any body the card shows lives in a calm grey panel.
 * Cancel is the ghost / softer-labelled affordance but still posts
 * ``verdict: 'deny'`` on the wire so the audit trail stays honest.
 *
 * Card variants:
 *
 *   * `request_human_approval` — the dedicated HITL gate. Title falls
 *     back to "Human approval requested" when no `proposed_action`
 *     is present. Body shows the agent's `reason`.
 *   * Any other gated tool — the server's `presentation.title` when it
 *     sent one, else "Approval required: <tool>"; the subtitle names the
 *     patient and visit the server read. The body is the presentation's
 *     blocks, else empty unless the tool opts in via `approval-input-details`.
 *
 * Resolution flow:
 *   1. Clinician taps Approve / Cancel.
 *   2. The ``onResolve`` callback (provided by the renderer) POSTs to
 *      the agent-run-scoped endpoint via ``useAgentRunChat``'s resolve
 *      wiring — the same endpoint the inbox drawer uses.
 *   3. The harness emits the resolution echo on its SSE; the timeline
 *      transitions the gating ``tool_use`` out of ``pending_approval``
 *      and this card unmounts.
 *
 * On the tools that opt into it (see `approval-note-opt-in`) the clinician
 * can attach a free-text ``reason`` to either verdict — the prompt instructs
 * the agent to incorporate it into the next ``✅ Done:`` line so the audit
 * trail is visible in the transcript.
 */

export type { ApprovalRequestInput } from './ApprovalRequestBlock.display';

/**
 * Minimal interface the inline approval card reads from the harness
 * ``ToolUseItem`` synthesis, plus what the composition point binds.
 */
export interface ApprovalRequestBlockProps {
  projectId?: string;
  agentRunId?: string;
  projectTitle?: string;
  approval: ApprovalRequestInput;
  onResolve: (
    approvalId: string,
    verdict: 'approve' | 'deny',
    reason?: string,
    modifiedInput?: Record<string, unknown> | null
  ) => Promise<void>;
  /** Optional warning rendered above the context panels. */
  warning?: string;
  /** Gates the Nango session-email card. The flag is integrations-owned, so it arrives resolved. */
  isNangoIntegrationsEnabled: boolean;
  /** The desktop-control lease, bound app-side. Module-level, so its identity is stable. */
  desktopControl: DesktopControlApprovalPort;
  /**
   * The composition point's check before a verdict posts (the template quality
   * attestation). A hook, module-level; resolving false keeps the gate open.
   */
  useApprovalGuard: () => (
    toolName: string,
    verdict: ApprovalVerdict
  ) => Promise<boolean>;
  /** The per-tool body, bound to the reads its rows need. Not drawn for `request_human_approval`. */
  renderInputDetails: (approval: ApprovalRequestInput) => ReactNode;
  /**
   * The patient-context card's app reads: the auth verdict and the patient
   * profile query. Module-level hooks, so the card calls stable references.
   */
  patientContext: Pick<PatientContextApprovalPorts, 'useAuth' | 'useProfile'>;
}

export const ApprovalRequestBlock = (props: ApprovalRequestBlockProps) => {
  const intl = useIntl();
  const presentation = useMemo(
    () =>
      readPresentation(
        props.approval.context ?? props.approval.pendingContext,
        props.approval.tool_name
      ),
    [props.approval.context, props.approval.pendingContext, props.approval.tool_name]
  );
  const generic = <GenericApprovalRequestBlock {...props} presentation={presentation} />;
  if (props.approval.tool_name === CREATE_SESSION_FROM_EHR_APPOINTMENT_TOOL) {
    return (
      <EhrSessionCreateApprovalBlock
        approval={props.approval}
        presentation={presentation}
        onResolve={props.onResolve}
        mark={<HeidiLogoMark className="size-5" />}
      />
    );
  }
  // An update the server described renders on the plain card, like a session
  // edit. One the server did not describe keeps the patient card, which reads
  // the profile itself.
  if (
    props.approval.tool_name === UPDATE_PATIENT_CONTEXT_TOOL_NAME &&
    !(
      presentation.title &&
      presentationSubject(intl, presentation.blocks) &&
      blocksOfKind(presentation.blocks, 'field_change').some(
        (change) => change.target === 'patient_profile'
      )
    )
  ) {
    return (
      <PatientContextApprovalRequest
        toolInput={props.approval.tool_input}
        projectId={props.projectId}
        approvalId={props.approval.approval_id}
        onResolve={props.onResolve}
        useProject={useProject}
        useAuth={props.patientContext.useAuth}
        useProfile={props.patientContext.useProfile}
        fallback={generic}
      />
    );
  }
  if (props.approval.tool_name === CREATE_PATIENT_PROFILE_TOOL_NAME) {
    return (
      <CreatePatientProfileApprovalRequest
        toolInput={props.approval.tool_input}
        approvalId={props.approval.approval_id}
        onResolve={props.onResolve}
        fallback={generic}
      />
    );
  }
  return props.isNangoIntegrationsEnabled &&
    isSessionEmailApprovalTool(props.approval.tool_name) ? (
    <SessionEmailApprovalCard approval={props.approval} onResolve={props.onResolve} />
  ) : (
    generic
  );
};

/**
 * Without a server title, a session edit's card says what the edit does in the
 * draft card's words ("Edit context", "Draft document: Referral letter"), so
 * the same edit reads the same in and out of a session chat. Without a subject,
 * its subtitle says an approval is required. `null` for every other tool, which
 * keeps `buildTitle`.
 */
const buildSessionEditHeader = (
  approval: ApprovalRequestInput,
  intl: IntlShape
): { title: string; subtitle: string } | null => {
  if (!isSessionDraftToolName(approval.tool_name)) return null;
  const draft = resolveDraftPresentation(approval.tool_name, approval.context?.draft);
  const spec = resolveDraftHeaderSpec(
    'pending_approval',
    draft,
    approval.tool_input ?? {},
    {
      toolName: approval.tool_name,
    }
  );
  return {
    title: formatDraftHeader(intl, spec),
    subtitle: intl.formatMessage({
      defaultMessage: 'Approval required',
      id: 'Uu6+o7gD87',
      description:
        'Subtitle of an approval card for a session edit, under what the edit does',
    }),
  };
};

const GenericApprovalRequestBlock = ({
  projectId,
  agentRunId,
  projectTitle,
  approval,
  onResolve,
  warning,
  desktopControl,
  useApprovalGuard,
  renderInputDetails,
  presentation,
}: ApprovalRequestBlockProps & { presentation: ApprovalPresentation }) => {
  const intl = useIntl();
  const [note, setNote] = useState('');
  const [pendingVerdict, setPendingVerdict] = useState<'approve' | 'deny' | null>(null);
  const guardApproval = useApprovalGuard();
  const { useBlockedMessage, useResolve } = desktopControl;
  const resolveDesktopControlledApproval = useResolve();

  const isProactive = approval.tool_name === REQUEST_HUMAN_APPROVAL_TOOL_NAME;
  const presentedHeader = readPresentedHeader(approval.tool_name, presentation, intl);
  // Session edit text, and a server body other than the patient profile
  // change, starts folded behind View once the header names its subject.
  const foldsDetails =
    isSessionDraftToolName(approval.tool_name) ||
    Boolean(
      presentedHeader?.subject &&
      hasPresentationDetails(presentation.blocks) &&
      !isPatientProfileChangeBody(presentation.blocks)
    );
  const [isEditShown, setIsEditShown] = useState(false);
  const acceptsNote = approvalAcceptsNote(approval.tool_name);
  const isComputerUseApproval = approval.tool_name === RUN_COMPUTER_USE_TOOL_NAME;
  const fields = readApprovalFields(approval, intl);
  const desktopControlledApproval = useMemo(
    () => ({
      approvalId: approval.approval_id,
      agentRunId,
      toolCallId: approval.tool_call_id,
      toolName: approval.tool_name,
      toolInput: approval.tool_input,
    }),
    [
      agentRunId,
      approval.approval_id,
      approval.tool_call_id,
      approval.tool_input,
      approval.tool_name,
    ]
  );
  const desktopControlBlockedMessage = useBlockedMessage(
    isComputerUseApproval ? desktopControlledApproval : null
  );

  const isBusy = pendingVerdict !== null;
  const isApproveBlocked = isComputerUseApproval && desktopControlBlockedMessage !== null;
  const submit = useCallback(
    (verdict: 'approve' | 'deny') => {
      if (isBusy) return;
      setPendingVerdict(verdict);

      const resolveApproval = async () => {
        if (!(await guardApproval(approval.tool_name, verdict))) {
          setPendingVerdict(null);
          return;
        }

        const result = await resolveDesktopControlledApproval({
          approval: desktopControlledApproval,
          verdict,
          owner: {
            projectId,
            projectTitle,
            label: fields.proposedAction,
          },
          resolve: async () => {
            await onResolve(
              approval.approval_id,
              verdict,
              note.trim() || undefined,
              null
            );
          },
        });
        if (!result.resolved) setPendingVerdict(null);
      };

      void resolveApproval().catch((err: unknown) => {
        setPendingVerdict(null);
        const message = err instanceof Error ? err.message : 'Unknown error';
        toast({
          title:
            verdict === 'approve'
              ? intl.formatMessage({
                  defaultMessage: "Couldn't approve",
                  id: 'NUA5UVs8zH',
                })
              : intl.formatMessage({
                  defaultMessage: "Couldn't cancel",
                  id: '0wRN6gSdFV',
                }),
          description: message,
          tone: 'destructive',
        });
      });
    },
    [
      approval.approval_id,
      approval.tool_name,
      desktopControlledApproval,
      fields.proposedAction,
      guardApproval,
      intl,
      isBusy,
      note,
      onResolve,
      projectId,
      projectTitle,
      resolveDesktopControlledApproval,
    ]
  );

  const sessionEditHeader = buildSessionEditHeader(approval, intl);
  const title =
    presentedHeader?.title ??
    sessionEditHeader?.title ??
    buildTitle(fields, isProactive, approval.tool_name, intl);
  const subtitle =
    presentedHeader?.subject ||
    (sessionEditHeader
      ? sessionEditHeader.subtitle
      : buildSubtitle(fields, isProactive, approval.tool_name, intl));

  return (
    <InlineApprovalCardShell
      title={title}
      subtitle={subtitle}
      urgent={!isBusy}
      data-testid={`approval-request-${approval.approval_id}`}
    >
      {warning && (
        <p
          className="rounded-md border border-validation-warning/30 bg-validation-warning/10 px-3 py-2 text-xs leading-snug text-text-primary"
          data-testid="approval-warning"
        >
          {warning}
        </p>
      )}

      {fields.reason && (
        <LabelledContextPanel
          label={intl.formatMessage({
            defaultMessage: 'Why ask',
            id: 'LA/j0s0JAk',
          })}
        >
          <p className="break-words text-sm leading-snug text-text-secondary [overflow-wrap:anywhere]">
            {fields.reason}
          </p>
        </LabelledContextPanel>
      )}

      {/* A `request_human_approval` gate carrying a server-authored
          presentation would otherwise grow a second body panel. */}
      {!isProactive && (!foldsDetails || isEditShown) && renderInputDetails(approval)}

      <div className="flex flex-col gap-3">
        {acceptsNote && (
          <Textarea
            minRows={1}
            maxRows={4}
            placeholder={intl.formatMessage(
              projectRunFailureMessages.approvalOptionalNote
            )}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={isBusy}
            className="border-border bg-surface text-sm"
          />
        )}
        <InlineApprovalActions
          onApprove={() => submit('approve')}
          onDeny={() => submit('deny')}
          pendingVerdict={pendingVerdict}
          disabled={isBusy}
          approveDisabled={isApproveBlocked}
          leading={
            foldsDetails ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-expanded={isEditShown}
                onClick={() => setIsEditShown((shown) => !shown)}
                data-testid="approval-view-edit"
              >
                {isEditShown
                  ? intl.formatMessage({
                      defaultMessage: 'Hide',
                      id: 'gSjMxB5e1g',
                      description:
                        'Approval card button that folds away what the call will change',
                    })
                  : intl.formatMessage({
                      defaultMessage: 'View',
                      id: '4tyOfnj3Di',
                      description:
                        'Approval card button that shows what the call will change',
                    })}
              </Button>
            ) : undefined
          }
        />
        {isApproveBlocked && (
          <div className="flex items-start gap-2 rounded-md border border-validation-warning-border bg-validation-warning-muted/40 px-2.5 py-1.5 text-xs text-validation-warning">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
            <span className="break-words">{desktopControlBlockedMessage}</span>
          </div>
        )}
      </div>
    </InlineApprovalCardShell>
  );
};
